import { VIEWFINDER_DIMENSIONS } from "../TrickStickerPicker/StickerDesigns";
import { serializeSvgElement, svgToPngDataUrl } from "../TrickStickerPicker/svgToPng";
import { StickerTransform } from "./useStickerGestures";

export interface PlacedStickerWithNode {
    id: string;
    natural: number;             // natural sticker size in canvas px (e.g. 180)
    transform: StickerTransform; // x,y are sticker CENTER in canvas px
    mountedSvg: SVGSVGElement;   // the already-rendered React <svg> DOM node
}

export interface CompositeOptions {
    canvasWidthCss: number;      // editor canvas width in CSS px
    canvasHeightCss: number;     // editor canvas height in CSS px
    includeViewfinder: boolean;
}

const OUTPUT_WIDTH = VIEWFINDER_DIMENSIONS.width;   // 1080
const OUTPUT_HEIGHT = VIEWFINDER_DIMENSIONS.height; // 1920

// Composite every placed sticker (plus optional ECLIPTIC viewfinder frame) into
// a single transparent 1080x1920 PNG suitable for IG Story stickerImage.
//
// The editor canvas is smaller than 1080x1920, so we scale every CSS-px
// position by the ratio between output and canvas dimensions.
export async function compositeStickersWithMountedNodes(
    placed: PlacedStickerWithNode[],
    viewfinderSvgNode: SVGSVGElement | null,
    opts: CompositeOptions,
): Promise<string> {
    if (opts.canvasWidthCss <= 0 || opts.canvasHeightCss <= 0) {
        throw new Error("Canvas dimensions must be positive");
    }

    const scaleX = OUTPUT_WIDTH / opts.canvasWidthCss;
    const scaleY = OUTPUT_HEIGHT / opts.canvasHeightCss;
    // Use the smaller axis scale for sticker size so circles don't stretch.
    const stickerScale = Math.min(scaleX, scaleY);

    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_WIDTH;
    canvas.height = OUTPUT_HEIGHT;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get 2d context for composite");
    ctx.clearRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);

    // Draw the viewfinder frame first (bottom layer) if enabled.
    if (opts.includeViewfinder && viewfinderSvgNode) {
        const markup = serializeSvgElement(viewfinderSvgNode);
        const dataUrl = await svgToPngDataUrl(markup, OUTPUT_WIDTH, OUTPUT_HEIGHT);
        const img = await loadImage(dataUrl);
        ctx.drawImage(img, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    }

    for (const p of placed) {
        const markup = serializeSvgElement(p.mountedSvg);
        // Rasterize at a generous size so rotations stay sharp.
        const rasterSize = Math.max(
            512,
            Math.round(p.natural * stickerScale * p.transform.scale * 2),
        );
        const dataUrl = await svgToPngDataUrl(markup, rasterSize, rasterSize);
        const img = await loadImage(dataUrl);

        const cx = p.transform.x * scaleX;
        const cy = p.transform.y * scaleY;
        const drawSize = p.natural * stickerScale * p.transform.scale;

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(p.transform.rotation);
        ctx.drawImage(img, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
        ctx.restore();
    }

    return canvas.toDataURL("image/png");
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Image load failed"));
        img.src = src;
    });
}
